/**
 * Unified Add to Cart Module
 * Provides consistent add to cart functionality across all components
 */

class UnifiedAddToCart {
  constructor() {
    this.isAdding = false;
    this.initializeEventListeners();
  }

  /**
   * Initialize global event listeners for add to cart actions
   */
  initializeEventListeners() {
    // Listen for custom add to cart events
    document.addEventListener('unified:addToCart', (e) => {
      const { variantId, quantity, button, successCallback, errorCallback } = e.detail;
      this.addToCart(variantId, quantity, button, successCallback, errorCallback);
    });

    // Initialize existing add to cart buttons
    this.initializeButtons();
  }

  /**
   * Initialize all add to cart buttons on the page
   */
  initializeButtons() {
    // Product cards add to cart buttons
    const cardButtons = document.querySelectorAll('[data-unified-add-to-cart]');
    cardButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = button.dataset.variantId;
        const quantity = parseInt(button.dataset.quantity || '1');

        this.addToCart(variantId, quantity, button);
      });
    });
  }

  /**
   * Add a product to the cart with unified loading states
   * @param {string|number} variantId - Product variant ID
   * @param {number} quantity - Quantity to add
   * @param {HTMLElement} button - Button element that triggered the action
   * @param {Function} successCallback - Optional callback on success
   * @param {Function} errorCallback - Optional callback on error
   */
  async addToCart(variantId, quantity = 1, button = null, successCallback = null, errorCallback = null) {
    // Prevent multiple simultaneous add to cart requests
    if (this.isAdding) return;
    this.isAdding = true;

    // Show loading state on button
    if (button) {
      this.showButtonLoading(button);
    }

    try {
      // Use the simpler format that works with both product forms and direct API calls
      const formData = {
        id: variantId,
        quantity: quantity
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle success
      this.handleSuccess(result, button, successCallback);

    } catch (error) {
      console.error('Add to cart error:', error);
      this.handleError(error, button, errorCallback);
    } finally {
      this.isAdding = false;

      // Remove loading state
      if (button) {
        setTimeout(() => {
          this.hideButtonLoading(button);
        }, 300); // Small delay for better UX
      }
    }
  }

  /**
   * Show loading state on button
   * @param {HTMLElement} button - Button element
   */
  showButtonLoading(button) {
    // Store original state
    button.dataset.originalText = button.textContent;
    button.dataset.originalDisabled = button.disabled;

    // Disable button
    button.disabled = true;
    button.classList.add('loading');

    // Check if button has a loading spinner
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    } else {
      // Add inline loading indicator if no spinner exists
      const textElement = button.querySelector('span') || button;
      if (textElement) {
        textElement.innerHTML = `
          <svg class="unified-loading-spinner" aria-hidden="true" focusable="false" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width: 16px; height: 16px; animation: spin 1s linear infinite;">
            <circle class="path" fill="none" stroke="currentColor" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
          <style>
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          </style>
        `;
      }
    }
  }

  /**
   * Hide loading state on button
   * @param {HTMLElement} button - Button element
   */
  hideButtonLoading(button) {
    // Remove loading class
    button.classList.remove('loading');

    // Hide spinner
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }

    // Restore original text if not showing success message
    if (!button.classList.contains('success')) {
      const textElement = button.querySelector('span') || button;
      if (textElement && button.dataset.originalText) {
        textElement.textContent = button.dataset.originalText;
      }
    }

    // Restore disabled state
    if (button.dataset.originalDisabled === 'false') {
      button.disabled = false;
    }
  }

  /**
   * Handle successful add to cart
   * @param {Object} result - Response from cart API
   * @param {HTMLElement} button - Button element
   * @param {Function} successCallback - Optional callback
   */
  handleSuccess(result, button, successCallback) {
    // Update cart count
    this.updateCartCount();

    // Trigger cart drawer or notification
    this.triggerCartUpdate(result);

    // Show "Added to Bag!" text on button
    if (button) {
      const textElement = button.querySelector('span') || button;
      if (textElement) {
        textElement.textContent = 'Added to Bag!';
        setTimeout(() => {
          if (button.dataset.originalText) {
            textElement.textContent = button.dataset.originalText;
          }
        }, 2000);
      }
    }

    // Call success callback if provided
    if (successCallback && typeof successCallback === 'function') {
      successCallback(result);
    }

    // Dispatch success event
    document.dispatchEvent(new CustomEvent('unified:cart:added', {
      detail: { cartData: result }
    }));
  }

  /**
   * Handle add to cart error
   * @param {Error} error - Error object
   * @param {HTMLElement} button - Button element
   * @param {Function} errorCallback - Optional callback
   */
  handleError(error, button, errorCallback) {
    // Show error state on button
    if (button) {
      button.classList.add('error');
      const textElement = button.querySelector('span') || button;
      const originalText = button.dataset.originalText || textElement.textContent;

      if (textElement) {
        textElement.textContent = 'Error - Try Again';

        // Reset after 3 seconds
        setTimeout(() => {
          button.classList.remove('error');
          textElement.textContent = originalText;
        }, 3000);
      }
    }

    // Call error callback if provided
    if (errorCallback && typeof errorCallback === 'function') {
      errorCallback(error);
    }

    // Dispatch error event
    document.dispatchEvent(new CustomEvent('unified:cart:error', {
      detail: { error: error.message }
    }));
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update all cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-icon-bubble .cart-count-bubble');
      cartCountElements.forEach((element) => {
        if (element.tagName === 'SPAN' && element.classList.contains('cart-count-bubble')) {
          element.textContent = cart.item_count;
          // Show/hide bubble based on count
          element.classList.toggle('cart-count-bubble--visible', cart.item_count > 0);
        } else {
          element.textContent = cart.item_count;
        }
      });

      // Dispatch cart count updated event
      document.dispatchEvent(new CustomEvent('cart:count:updated', {
        detail: { count: cart.item_count, cart }
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Trigger cart drawer or notification
   */
  triggerCartUpdate() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Fetch fresh cart-drawer section HTML and replace entire cart-drawer content
    // (handles empty-to-filled transition correctly)
    fetch(window.location.pathname + '?section_id=cart-drawer')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newCartDrawer = doc.querySelector('cart-drawer');
        if (newCartDrawer) {
          cartDrawer.innerHTML = newCartDrawer.innerHTML;
          // Re-execute inline scripts (innerHTML scripts don't run automatically)
          cartDrawer.querySelectorAll('script').forEach(function(oldScript) {
            var newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
        }
        cartDrawer.classList.remove('is-empty');
        // Re-bind overlay close listener after full content replacement
        var overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', function() { cartDrawer.close(); });
        }
        cartDrawer.open();
        // Load cart recommendations (works even on first add to empty cart)
        if (window.loadCartRecommendations) window.loadCartRecommendations();
      })
      .catch(function(err) { console.error('Cart drawer refresh error:', err); });

    // Update cart icon bubble
    fetch(window.location.pathname + '?section_id=cart-icon-bubble')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newBubble = doc.querySelector('.shopify-section');
        var currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      })
      .catch(function(err) { console.error('Cart bubble refresh error:', err); });
  }

  /**
   * Public method to programmatically add to cart
   * @param {Object} options - Add to cart options
   */
  add(options) {
    const { variantId, quantity = 1, button = null, onSuccess = null, onError = null } = options;
    return this.addToCart(variantId, quantity, button, onSuccess, onError);
  }
}

// Initialize when DOM is ready
function initUnifiedAddToCart() {
  window.unifiedAddToCart = new UnifiedAddToCart();
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnifiedAddToCart);
} else {
  initUnifiedAddToCart();
}

// Export for use in other modules
window.UnifiedAddToCart = UnifiedAddToCart;